import type { BoardParticipant, CardParticipation, CardRelation, LaneChange, ResolvedBranding } from '@kaba/shared';
import { MutationCache, QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  byPosition,
  keyBetween,
  type Board,
  type BoardEvent,
  type BoardSnapshot,
  type CardDetail,
  type CardSummary,
  type Column,
  type CustomField,
  type Label,
  type SavedView,
  type UpdateCardInput,
} from '@kaba/shared';
import { queueIdChange, withPendingIds } from './cardIdOps';
import { api, ApiError, newOpId } from './api';
import type { CardOpInput, NewOutboxOp } from './outbox';
import { clearSaveError, mutationCardId, reportSaveError } from './saveState';
import { toast } from './toast';
import { askGateOverride, gateOf } from './gateOverride';

const isSave = (key: readonly unknown[] | undefined) => key?.[0] === 'save';

/**
 * Offline-Anbindung (0.44): wird von `offlineSync.ts` beim Start eingesetzt – so entsteht kein
 * Importkreis (board → offlineSync → realtime → board). Ohne Anbindung verhält sich alles wie online.
 */
export interface OfflineHooks {
  mustQueue: (cardId: string) => boolean;
  queueUpdate: (boardId: string, cardId: string, patch: UpdateCardInput, before?: CardSummary) => Promise<void>;
  queueMove: (boardId: string, cardId: string, columnId: string, beforeId: string | null, afterId: string | null) => Promise<void>;
  createOffline: (boardId: string, input: { columnId: string; title: string; placement?: 'top' | 'bottom' }, id: string, opId: string) => Promise<CardSummary>;
  isOfflineCreated: (cardId: string) => boolean;
  queueCardOp: (op: NewOutboxOp, what: string) => Promise<void>;
}
let hooks: OfflineHooks = {
  mustQueue: () => false,
  queueUpdate: async () => undefined,
  queueMove: async () => undefined,
  createOffline: () => Promise.reject(new ApiError(0, 'Keine Verbindung zum Server')),
  isOfflineCreated: () => false,
  queueCardOp: () => Promise.reject(new ApiError(0, 'Keine Verbindung zum Server')),
};
export function setOfflineHooks(h: OfflineHooks) {
  hooks = h;
}
const mustQueue = (id: string) => hooks.mustQueue(id);
/** Muss eine Änderung dieser Karte in die Offline-Reihe (keine Verbindung oder noch Ausstehendes)? */
export const cardMustQueue = mustQueue;
const queueUpdate: OfflineHooks['queueUpdate'] = (...a) => hooks.queueUpdate(...a);
const queueMove: OfflineHooks['queueMove'] = (...a) => hooks.queueMove(...a);
const createOffline: OfflineHooks['createOffline'] = (...a) => hooks.createOffline(...a);
const isOfflineCreated = (id: string) => hooks.isOfflineCreated(id);
export const isNetworkError = (e: unknown) => e instanceof ApiError && e.status === 0;

/**
 * Online senden – oder ablegen, wenn offline bzw. für die Karte schon etwas aussteht (0.45).
 * `queue` erhält die Ablage ohne ID; die opId wird hier vergeben. Gibt true zurück, wenn abgelegt.
 */
export async function sendOrQueue(cardId: string, send: () => Promise<unknown>, op: CardOpInput, what: string): Promise<boolean> {
  const queue = () => hooks.queueCardOp({ ...op, id: newOpId(), label: '' } as NewOutboxOp, what).then(() => true);
  if (hooks.mustQueue(cardId)) return queue();
  try {
    await send();
    return false;
  } catch (err) {
    if (isNetworkError(err)) return queue();
    throw err;
  }
}

/** UUID v4 – auch ohne crypto.randomUUID (http im LAN) */
export function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const queryClient = new QueryClient({
  // Speicherstatus: Fehler bleibt stehen, bis wieder etwas gespeichert wurde (getrennt von der Live-Verbindung)
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, m) => {
      if (!isSave(m.options.mutationKey)) return;
      const cardId = mutationCardId(m);
      const e = err as { inline?: boolean; status?: number; message?: string };
      if (e.inline) return; // wird an Ort und Stelle angezeigt (z. B. Beschreibung mit Konflikt)
      reportSaveError(
        e.status === 409
          ? 'Nicht gespeichert: Jemand anderes hat gleichzeitig geändert. Bitte erneut eintragen.'
          : e.status === 0 || e.status === undefined
            ? 'Nicht gespeichert: keine Verbindung zum Server.'
            : `Nicht gespeichert: ${e.message ?? 'Unbekannter Fehler'}`,
        cardId,
      );
    },
    onSuccess: (_d, _v, _c, m) => {
      if (isSave(m.options.mutationKey)) clearSaveError(mutationCardId(m));
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: (n, err) => !(err instanceof ApiError && err.status < 500 && err.status > 0) && n < 2 },
  },
});

export const boardKey = (id: string) => ['board', id] as const;
export const cardKey = (id: string) => ['card', id] as const;

type SnapshotListener = (boardId: string, lastEventId: number) => void;
const snapshotListeners = new Set<SnapshotListener>();
/** Wird nach jedem (Neu-)Laden eines Board-Stands aufgerufen – die Echtzeit abonniert dann ab diesem Stand neu */
export function onSnapshotLoaded(fn: SnapshotListener) {
  snapshotListeners.add(fn);
  return () => snapshotListeners.delete(fn);
}

export function useBoard(id: string) {
  return useQuery({
    queryKey: boardKey(id),
    queryFn: async () => {
      const snap = await api.get<BoardSnapshot>(`/api/boards/${id}`);
      // Erst nachdem der Stand im Cache liegt (setTimeout), ab seinem Ereignisstand neu abonnieren:
      // Ereignisse, die während des Ladens kamen, werden so erneut geliefert statt überschrieben.
      setTimeout(() => snapshotListeners.forEach((fn) => fn(id, snap.lastEventId)), 0);
      return snap;
    },
  });
}

export function useBoards() {
  return useQuery({ queryKey: ['boards'], queryFn: () => api.get<Board[]>('/api/boards') });
}

export function useCard(id: string | null) {
  return useQuery({
    queryKey: cardKey(id ?? 'none'),
    queryFn: async () => {
      try {
        return withPendingIds(id!, await api.get<CardDetail>(`/api/cards/${id}`));
      } catch (err) {
        // Offline angelegt und noch nicht übertragen: Details aus dem Board-Stand (0.44)
        const local = id ? offlineCreatedDetail(id) : null;
        if (local && (isNetworkError(err) || (err instanceof ApiError && err.status === 404))) return local;
        throw err;
      }
    },
    enabled: !!id,
  });
}

function offlineCreatedDetail(id: string): CardDetail | null {
  if (!isOfflineCreated(id)) return null;
  for (const [, snap] of queryClient.getQueriesData<BoardSnapshot>({ queryKey: ['board'] })) {
    const c = snap?.cards?.find((x) => x.id === id);
    if (c) return { ...c, description: null, descriptionRev: -1, checklist: [], attachments: [], createdAt: c.updatedAt, createdBy: null };
  }
  return null;
}

/* ---------- Snapshot-Helfer ---------- */

function updateSnapshot(qc: QueryClient, boardId: string, fn: (s: BoardSnapshot) => BoardSnapshot) {
  qc.setQueryData<BoardSnapshot>(boardKey(boardId), (s) => (s ? fn(s) : s));
}

function upsert<T extends { id: string }>(list: T[], item: T): T[] {
  const i = list.findIndex((x) => x.id === item.id);
  if (i < 0) return [...list, item];
  const copy = list.slice();
  copy[i] = item;
  return copy;
}

export function upsertCards(qc: QueryClient, boardId: string, cards: (CardSummary | null | undefined)[]) {
  updateSnapshot(qc, boardId, (s) => {
    let list = s.cards;
    for (const c of cards) {
      if (!c) continue;
      // Offene eigene Label-/Zuständigenänderungen bleiben sichtbar (L01)
      list = c.archivedAt ? list.filter((x) => x.id !== c.id) : upsert(list, withPendingIds(c.id, c));
    }
    return { ...s, cards: list.sort(byPosition) };
  });
  for (const c of cards) {
    if (!c) continue;
    qc.setQueryData<CardDetail>(cardKey(c.id), (d) => (d ? withPendingIds(c.id, { ...d, ...c }) : d));
  }
}

/** Ereignis aus dem Echtzeitkanal anwenden. Gibt false zurück, wenn neu geladen werden muss. */
export function applyEvent(qc: QueryClient, e: BoardEvent): void {
  const snap = qc.getQueryData<BoardSnapshot>(boardKey(e.boardId));
  if (!snap) return;
  if (e.id <= snap.lastEventId) return; // bereits angewendet
  updateSnapshot(qc, e.boardId, (s) => {
    let next: BoardSnapshot = { ...s, lastEventId: e.id };
    switch (e.type) {
      case 'card.upserted': {
        const card = e.payload as CardSummary;
        next = card.archivedAt
          ? { ...next, cards: next.cards.filter((c) => c.id !== card.id) }
          : { ...next, cards: upsert(next.cards, card).sort(byPosition) };
        break;
      }
      case 'card.deleted':
        next = { ...next, cards: next.cards.filter((c) => c.id !== e.entityId) };
        if (next.participations) next = { ...next, participations: next.participations.filter((p) => p.cardId !== e.entityId) };
        // Archivieren stoppt Timer auf der Karte – Timerleiste aktualisieren
        if (qc.getQueryData<{ cardId: string } | null>(['timer'])?.cardId === e.entityId) void qc.invalidateQueries({ queryKey: ['timer'] });
        break;
      case 'column.upserted':
        next = { ...next, columns: upsert(next.columns, e.payload as Column).sort(byPosition) };
        // Spaltenregeln können Handlungshinweise offener Karten ändern
        void qc.invalidateQueries({ queryKey: ['card'] });
        break;
      case 'column.deleted':
        next = { ...next, columns: next.columns.filter((c) => c.id !== e.entityId) };
        // Regeln mit diesem Verweis müssen repariert werden (0.59)
        void qc.invalidateQueries({ queryKey: ['automations', e.boardId] });
        void qc.invalidateQueries({ queryKey: ['automation-buttons', e.boardId] });
        break;
      case 'label.upserted':
        next = { ...next, labels: upsert(next.labels, e.payload as Label) };
        break;
      case 'label.deleted':
        next = {
          ...next,
          labels: next.labels.filter((l) => l.id !== e.entityId),
          cards: next.cards.map((c) => ({ ...c, labelIds: c.labelIds.filter((x) => x !== e.entityId) })),
        };
        void qc.invalidateQueries({ queryKey: ['automations', e.boardId] });
        break;
      case 'board.updated': {
        const b = e.payload as Board;
        // Pfad und Archivstand sind personenbezogen: nach dem Verschieben bzw. Archivieren neu laden
        // 0.90 (K3): Beteiligte ein-/ausgeschaltet – Liste aus dem Board-Stand neu laden
        if (b.participantsEnabled !== undefined && !!b.participantsEnabled !== !!next.board.participantsEnabled && !b.participantsEnabled) next = { ...next, participants: undefined, participations: undefined };
        if (
          (b.placementVersion !== undefined && b.placementVersion !== next.board.placementVersion) ||
          !!b.archivedAt !== !!next.board.archivedAt ||
          (b.participantsEnabled !== undefined && !!b.participantsEnabled !== !!next.board.participantsEnabled && b.participantsEnabled)
        ) {
          void qc.invalidateQueries({ queryKey: boardKey(e.boardId) });
        }
        void qc.invalidateQueries({ queryKey: ['boards'] });
        void qc.invalidateQueries({ queryKey: ['nav'] });
        next = { ...next, board: { ...next.board, ...b, role: next.board.role, placement: next.board.placement } };
        break;
      }
      case 'field.upserted':
        next = { ...next, fields: upsert(next.fields, e.payload as CustomField).sort(byPosition) };
        break;
      case 'field.deleted':
        next = { ...next, fields: next.fields.filter((f) => f.id !== e.entityId) };
        void qc.invalidateQueries({ queryKey: ['automations', e.boardId] });
        break;
      case 'comment.changed':
        if (e.entityId) void qc.invalidateQueries({ queryKey: ['comments', e.entityId] });
        break;
      case 'view.upserted': {
        const v = e.payload as SavedView;
        const i = next.views.findIndex((x) => x.id === v.id);
        const views = i < 0 ? [...next.views, v] : next.views.map((x) => (x.id === v.id ? v : x));
        next = { ...next, views: views.sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0)) };
        break;
      }
      case 'view.deleted':
        next = { ...next, views: next.views.filter((v) => v.id !== e.entityId) };
        break;
      case 'templates.changed':
        void qc.invalidateQueries({ queryKey: boardKey(e.boardId) });
        break;
      case 'library.changed':
        void qc.invalidateQueries({ queryKey: ['library', e.boardId] });
        // 0.58: Kartendetails zeigen „Neuere Fassung“ – offene Karten neu laden
        void qc.invalidateQueries({ queryKey: ['card'] });
        break;
      case 'automations.changed':
        void qc.invalidateQueries({ queryKey: ['automations', e.boardId] });
        break;
      case 'branding.changed':
        // Nur die Gestaltung neu laden – keine Neumontage des Boards (T07)
        void api
          .get<ResolvedBranding>(`/api/boards/${e.boardId}/branding/resolved`)
          .then((b) => qc.setQueryData<BoardSnapshot>(boardKey(e.boardId), (s) => (s ? { ...s, branding: b } : s)))
          .catch(() => undefined);
        void qc.invalidateQueries({ queryKey: ['boards'] });
        void qc.invalidateQueries({ queryKey: ['branding', 'board', e.boardId] });
        break;
      // 0.89 (K2): beschreibende Beziehungen; (K1) Kontext neu laden; (K4) Spaltenregeln ändern Hinweise
      case 'relation.upserted': {
        const r = e.payload as CardRelation;
        next = { ...next, relations: [...(next.relations ?? []).filter((x) => x.id !== r.id), r] };
        break;
      }
      case 'relation.deleted':
        next = { ...next, relations: (next.relations ?? []).filter((x) => x.id !== e.entityId) };
        break;
      case 'context.changed':
        void qc.invalidateQueries({ queryKey: ['context', e.boardId] });
        break;
      // 0.90 (K3): Beteiligte ohne Konto
      case 'participant.upserted': {
        const p = e.payload as BoardParticipant;
        next = { ...next, participants: [...(next.participants ?? []).filter((x) => x.id !== p.id), p] };
        break;
      }
      case 'participant.deleted':
        next = { ...next, participants: (next.participants ?? []).filter((x) => x.id !== e.entityId), participations: (next.participations ?? []).filter((x) => x.participantId !== e.entityId) };
        break;
      case 'participation.upserted': {
        const p = e.payload as CardParticipation;
        next = { ...next, participations: [...(next.participations ?? []).filter((x) => x.id !== p.id), p] };
        break;
      }
      case 'participation.deleted':
        next = { ...next, participations: (next.participations ?? []).filter((x) => x.id !== e.entityId) };
        break;
      case 'members.changed':
        void qc.invalidateQueries({ queryKey: boardKey(e.boardId) });
        void qc.invalidateQueries({ queryKey: ['sharing', e.boardId] });
        void qc.invalidateQueries({ queryKey: ['access', e.boardId] });
        break;
    }
    return next;
  });
  if (e.type === 'card.upserted' && e.entityId) {
    const card = e.payload as CardSummary;
    qc.setQueryData<CardDetail>(cardKey(e.entityId), (d) => (d ? { ...d, ...card } : d));
    // Details (Checkliste, Anhänge) könnten sich geändert haben
    if (qc.getQueryData(cardKey(e.entityId))) {
      void qc.invalidateQueries({ queryKey: cardKey(e.entityId) });
      void qc.invalidateQueries({ queryKey: ['activity', e.entityId] });
    }
  }
}

/* ---------- Mutationen ---------- */

function onFail(qc: QueryClient, boardId: string, err: unknown) {
  const msg = isNetworkError(err)
    ? 'Ohne Verbindung geht das nicht. Offline speichert KaBa neue Karten, Verschieben, Archivieren, Kartenangaben, eigene Felder, Labels, Zuständige, Checkliste und Kommentare ohne Dateien.'
    : err instanceof Error
      ? err.message
      : 'Unbekannter Fehler';
  toast.error(`Nicht gespeichert: ${msg}`);
  void qc.invalidateQueries({ queryKey: boardKey(boardId) });
}

export interface MoveTarget {
  /** keine Hinweise zu Spaltenregeln (z. B. Rückgängig) */
  silent?: boolean;
  /** 0.81: Abfrage der Zielspalte schon beantwortet (oder Rückgängig) */
  asked?: boolean;
  cardId: string;
  columnId: string;
  /** Karte direkt davor bzw. danach (null = Anfang/Ende) */
  beforeId: string | null;
  afterId: string | null;
}

/** Nachbarn für eine Zielposition (Index ohne die bewegte Karte) */
export function neighborsAt(cards: CardSummary[], columnId: string, cardId: string, index: number) {
  const others = cards.filter((c) => c.columnId === columnId && c.id !== cardId).sort(byPosition);
  return { beforeId: others[index - 1]?.id ?? null, afterId: others[index]?.id ?? null, count: others.length };
}

/** Karten, die ich gerade selbst geändert habe (für „Karte passt nicht mehr zum Filter“) */
const localEdits = new Map<string, number>();
export function noteLocalEdit(cardId: string) {
  localEdits.set(cardId, Date.now());
}
export function editedLocally(cardId: string, withinMs = 8000): boolean {
  const t = localEdits.get(cardId);
  return !!t && Date.now() - t < withinMs;
}

/** Archivieren – auch offline (0.46). Gibt den Stand davor zurück (für „Rückgängig“) */
export async function archiveCard(qc: QueryClient, boardId: string, cardId: string): Promise<CardSummary | undefined> {
  const before = qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === cardId);
  await sendOrQueue(cardId, () => api.del(`/api/cards/${cardId}`), { kind: 'archive', boardId, cardId }, 'archiviert');
  qc.setQueryData<BoardSnapshot>(boardKey(boardId), (s) => (s ? { ...s, cards: s.cards.filter((c) => c.id !== cardId) } : s));
  return before;
}

/** Wiederherstellen – auch offline; die Karte erscheint sofort wieder */
export async function restoreCard(qc: QueryClient, boardId: string, card: CardSummary | undefined, cardId: string) {
  if (card) upsertCards(qc, boardId, [{ ...card, archivedAt: null }]);
  let res: CardSummary | undefined;
  const queued = await sendOrQueue(cardId, async () => (res = await api.post<CardSummary>(`/api/cards/${cardId}/restore`)), { kind: 'restore', boardId, cardId }, 'wiederhergestellt');
  if (!queued && res) upsertCards(qc, boardId, [res]);
}

/** Verschiebt eine Karte: sofort lokal, dann Server; bei Fehler zurück zum Serverstand */
export function useMoveCard(boardId: string) {
  const qc = useQueryClient();
  // 0.81: nach der Abfrage der Zielspalte erneut verschieben (gleiche Mutation, damit der Speicherstand stimmt)
  const moveRef = useRef<((t: MoveTarget) => void) | null>(null);
  const mutation = useMutation({
    mutationKey: ['save'],
    mutationFn: async (t: MoveTarget) => {
      noteLocalEdit(t.cardId);
      const snap = qc.getQueryData<BoardSnapshot>(boardKey(boardId));
      // 0.81: Zielspalte fragt nach Feldern – erst der Dialog, dann (mit den Werten) verschieben
      const moving = snap?.cards.find((c) => c.id === t.cardId);
      const target = snap?.columns.find((c) => c.id === t.columnId);
      if (!t.asked && !t.silent && moving && target?.entryAsk && moving.columnId !== t.columnId) {
        const fields = target.entryAsk.fieldIds.map((id) => snap!.fields.find((f) => f.id === id)).filter((f): f is NonNullable<typeof f> => !!f);
        if (fields.length) {
          const { askEntryFields } = await import('./entryAsk');
          askEntryFields({
            card: moving,
            column: target,
            fields,
            required: target.entryAsk.required,
            members: snap!.members,
            done: async (values) => {
              const { saveCardField } = await import('./cardFields');
              let cur = moving;
              for (const f of fields) {
                const v = values[f.id] ?? null;
                if (JSON.stringify(v) === JSON.stringify(cur.custom[f.id] ?? null)) continue;
                await saveCardField(qc, boardId, cur, f, v);
                cur = qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === t.cardId) ?? cur;
              }
              moveRef.current?.({ ...t, asked: true });
            },
          });
          return { cards: [] as CardSummary[], asked: true };
        }
      }
      const before = t.beforeId ? (snap?.cards.find((c) => c.id === t.beforeId) ?? null) : null;
      const after = t.afterId ? (snap?.cards.find((c) => c.id === t.afterId) ?? null) : null;
      // Optimistisch
      const card = snap?.cards.find((c) => c.id === t.cardId);
      if (card) {
        let position: string;
        try {
          position = keyBetween(before?.position, after?.position);
        } catch {
          position = card.position;
        }
        upsertCards(qc, boardId, [{ ...card, columnId: t.columnId, position }]);
      }
      // Offline (oder ältere Änderungen dieser Karte stehen noch aus): ablegen, später übertragen (0.44)
      if (mustQueue(t.cardId)) {
        await queueMove(boardId, t.cardId, t.columnId, before?.id ?? null, after?.id ?? null);
        return { cards: [] as CardSummary[], queued: true };
      }
      const opId = newOpId();
      let res: { cards: CardSummary[] };
      try {
        res = await api.post<{ cards: CardSummary[] }>(`/api/cards/${t.cardId}/move`, { columnId: t.columnId, beforeId: before?.id ?? null, afterId: after?.id ?? null, opId });
      } catch (err) {
        // 0.79: verbindliche Spaltenregel – kein Speicherfehler, sondern ein Hinweis (ggf. mit „Trotzdem …“)
        if (gateOf(err)) {
          handleGateBlock(qc, boardId, t, err as Error);
          return { cards: [] as CardSummary[], gated: true };
        }
        if (!isNetworkError(err)) throw err;
        await queueMove(boardId, t.cardId, t.columnId, before?.id ?? null, after?.id ?? null);
        return { cards: [] as CardSummary[], queued: true };
      }
      upsertCards(qc, boardId, res.cards);
      if (card && card.columnId !== t.columnId && !t.silent) announceColumnRules(qc, boardId, card, t.columnId);
      return res;
    },
    onError: (err) => onFail(qc, boardId, err),
  });
  moveRef.current = mutation.mutate;
  return mutation;
}

/** 0.79: Verschieben scheitert an einer verbindlichen Spaltenregel – zurücksetzen, Grund nennen, ggf. übergehen */
function handleGateBlock(qc: QueryClient, boardId: string, t: MoveTarget, err: Error) {
  const gate = gateOf(err)!;
  void qc.invalidateQueries({ queryKey: boardKey(boardId) });
  const message = err.message;
  if (!gate.canOverride) {
    toast.error(message);
    return;
  }
  toast.info(message, {
    label: 'Trotzdem …',
    run: () =>
      askGateOverride(message, async (reason) => {
        try {
          const r = await api.post<{ cards: CardSummary[] }>(`/api/cards/${t.cardId}/move`, {
            columnId: t.columnId,
            beforeId: t.beforeId,
            afterId: t.afterId,
            opId: newOpId(),
            override: { reason },
          });
          upsertCards(qc, boardId, r.cards);
        } catch (e) {
          onFail(qc, boardId, e);
        }
      }),
  });
}

/**
 * Spaltenregeln (Definition of Done): Beim Wechsel kurz anzeigen, was die Spalten erwarten –
 * nicht blockierend, mit „Rückgängig“ an die alte Stelle.
 */
function announceColumnRules(qc: QueryClient, boardId: string, card: CardSummary, toColumnId: string) {
  const snap = qc.getQueryData<BoardSnapshot>(boardKey(boardId));
  const from = snap?.columns.find((c) => c.id === card.columnId);
  const to = snap?.columns.find((c) => c.id === toColumnId);
  const parts = [
    from?.exitRule ? `„${from.title}“ verlassen: ${from.exitRule}` : null,
    to?.entryRule ? `„${to.title}“ übernehmen: ${to.entryRule}` : null,
  ].filter(Boolean);
  if (!parts.length || !from) return;
  // Alte Nachbarn für „Rückgängig“
  const old = (snap?.cards ?? []).filter((c) => c.columnId === from.id && c.id !== card.id).sort(byPosition);
  const prev = old.filter((c) => c.position < card.position).at(-1) ?? null;
  const next = old.find((c) => c.position > card.position) ?? null;
  toast.info(parts.join(' · '), {
    label: 'Rückgängig',
    run: () =>
      void api
        .post<{ cards: CardSummary[] }>(`/api/cards/${card.id}/move`, { columnId: from.id, beforeId: prev?.id ?? null, afterId: next?.id ?? null, opId: newOpId() })
        .then((r) => upsertCards(qc, boardId, r.cards))
        .catch((err) => onFail(qc, boardId, err)),
  });
}

export function useMoveColumn(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['save'],
    mutationFn: async ({ columnId, index }: { columnId: string; index: number }) => {
      const snap = qc.getQueryData<BoardSnapshot>(boardKey(boardId));
      const others = (snap?.columns ?? []).filter((c) => c.id !== columnId).sort(byPosition);
      const before = others[index - 1] ?? null;
      const after = others[index] ?? null;
      const col = snap?.columns.find((c) => c.id === columnId);
      if (col) {
        try {
          const position = keyBetween(before?.position, after?.position);
          updateSnapshot(qc, boardId, (s) => ({ ...s, columns: upsert(s.columns, { ...col, position }).sort(byPosition) }));
        } catch {
          /* Server verteilt neu */
        }
      }
      const res = await api.post<Column[]>(`/api/columns/${columnId}/move`, {
        beforeId: before?.id ?? null,
        afterId: after?.id ?? null,
        opId: newOpId(),
      });
      updateSnapshot(qc, boardId, (s) => {
        let cols = s.columns;
        for (const c of res) cols = upsert(cols, c);
        return { ...s, columns: cols.sort(byPosition) };
      });
    },
    onError: (err) => onFail(qc, boardId, err),
  });
}

export function useUpdateCard(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['save'],
    mutationFn: async ({ id, patch, inlineConflict }: { id: string; patch: UpdateCardInput; inlineConflict?: boolean }) => {
      noteLocalEdit(id);
      const snap = qc.getQueryData<BoardSnapshot>(boardKey(boardId));
      const card = snap?.cards.find((c) => c.id === id);
      if (card) {
        const optimistic: CardSummary = { ...card };
        if (patch.title !== undefined) optimistic.title = patch.title;
        if (patch.dueAt !== undefined) optimistic.dueAt = patch.dueAt;
        if (patch.startAt !== undefined) optimistic.startAt = patch.startAt;
        if (patch.startHasTime !== undefined) optimistic.startHasTime = patch.startHasTime;
        if (patch.startAt === null) optimistic.startHasTime = false;
        if (patch.dueHasTime !== undefined) optimistic.dueHasTime = patch.dueHasTime;
        if (patch.priority !== undefined) optimistic.priority = patch.priority;
        if (patch.ownerId !== undefined) {
          optimistic.ownerId = patch.ownerId;
          if (patch.ownerId && !optimistic.assigneeIds.includes(patch.ownerId)) optimistic.assigneeIds = [...optimistic.assigneeIds, patch.ownerId];
        }
        if (patch.blocked !== undefined) optimistic.blocked = patch.blocked;
        if (patch.blockedReason !== undefined) optimistic.blockedReason = patch.blockedReason;
        if (patch.completed !== undefined) optimistic.completedAt = patch.completed ? new Date().toISOString() : null;
        if (patch.description !== undefined) optimistic.hasDescription = !!patch.description?.trim();
        upsertCards(qc, boardId, [optimistic]);
        qc.setQueryData<CardDetail>(cardKey(id), (d) =>
          d ? { ...d, ...optimistic, description: patch.description !== undefined ? patch.description : d.description } : d,
        );
      }
      // Titelbild braucht den Server; alles andere lässt sich offline ablegen (0.44)
      const queueable = patch.cover === undefined;
      if (queueable && mustQueue(id)) {
        await queueUpdate(boardId, id, patch, card);
        return qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === id) ?? null;
      }
      try {
        const res = await api.patch<CardSummary>(`/api/cards/${id}`, { ...patch, opId: newOpId() });
        upsertCards(qc, boardId, [res]);
        return res;
      } catch (err) {
        if (queueable && isNetworkError(err)) {
          await queueUpdate(boardId, id, patch, card);
          return qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === id) ?? null;
        }
        if (err instanceof ApiError && err.status === 409) {
          const data = err.data as { card?: CardSummary; detail?: CardDetail };
          if (data.card) upsertCards(qc, boardId, [data.card]);
          if (data.detail) qc.setQueryData(cardKey(id), data.detail);
          if (inlineConflict) throw Object.assign(err, { handled: true, inline: true });
          toast.error('Jemand anderes hat diese Karte gerade geändert. Der aktuelle Stand wurde geladen – deine Änderung bitte erneut eintragen.');
          throw Object.assign(err, { handled: true });
        }
        throw err;
      }
    },
    onError: (err) => {
      if ((err as { handled?: boolean }).handled) return;
      onFail(qc, boardId, err);
    },
  });
}

export function useCreateCard(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['save'],
    mutationFn: async (input: { columnId: string; title: string; placement?: 'top' | 'bottom'; templateId?: string }) => {
      // Die ID vergibt der Browser – so bleibt eine offline angelegte Karte dieselbe (0.44)
      const id = newUuid();
      const opId = newOpId();
      const offline = async () => {
        const card = await createOffline(boardId, input, id, opId);
        window.dispatchEvent(new CustomEvent('kaba-card-created', { detail: card.id }));
        return card;
      };
      // Vorlagen setzen Werte auf dem Server – nur ohne Vorlage offline
      if (!input.templateId && typeof navigator !== 'undefined' && navigator.onLine === false) return offline();
      let res: CardSummary;
      try {
        res = await api.post<CardSummary>(`/api/boards/${boardId}/cards`, { ...input, id, opId });
      } catch (err) {
        if (!input.templateId && isNetworkError(err)) return offline();
        throw err;
      }
      noteLocalEdit(res.id);
      upsertCards(qc, boardId, [res]);
      // Eigene neue Karte immer einhängen, auch am Ende langer Spalten (BoardDnd)
      window.dispatchEvent(new CustomEvent('kaba-card-created', { detail: res.id }));
      return res;
    },
    onError: (err) => onFail(qc, boardId, err),
  });
}

/** Allgemeine Mutation mit anschließendem Neuladen der Karte; mit `cardId` zählt sie zum Speicherstatus dieser Karte */
export function useCardAction(boardId: string, cardId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['save', cardId ?? null],
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: (res) => {
      if (res && typeof res === 'object' && 'boardId' in res && 'columnId' in res) upsertCards(qc, boardId, [res as CardSummary]);
    },
    onError: (err) => onFail(qc, boardId, err),
  });
}

export function useBoardAction(boardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['save'],
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSettled: () => qc.invalidateQueries({ queryKey: boardKey(boardId) }),
    onError: (err) => onFail(qc, boardId, err),
  });
}

/** Karte in eine andere Zeile gezogen: Eigenschaft optimistisch ändern und speichern */
export function useLaneChange(boardId: string) {
  const qc = useQueryClient();
  const update = useUpdateCard(boardId);
  return (card: CardSummary, change: LaneChange) => {
    if (change.type === 'owner') return update.mutate({ id: card.id, patch: { ownerId: change.ownerId } });
    if (change.type === 'priority') return update.mutate({ id: card.id, patch: { priority: change.priority } });
    const before = card.labelIds;
    const save = (ids: string[]) => {
      noteLocalEdit(card.id);
      const cur = qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.cards.find((c) => c.id === card.id);
      const was = cur?.labelIds ?? before;
      if (cur) upsertCards(qc, boardId, [{ ...cur, labelIds: ids }]);
      // Als Änderung speichern (0.45): Labels, die andere inzwischen gesetzt haben, bleiben
      const change = { add: ids.filter((x) => !was.includes(x)), remove: was.filter((x) => !ids.includes(x)) };
      return queueIdChange(card.id, 'labels', change, () =>
        sendOrQueue(
          card.id,
          () => api.patch<CardSummary>(`/api/cards/${card.id}/labels`, change).then((r) => upsertCards(qc, boardId, [r])),
          { kind: 'labels', boardId, cardId: card.id, ...change },
          'Labels geändert',
        ),
      ).catch((err) => onFail(qc, boardId, err));
    };
    void save(change.labelIds);
    const removed = before.filter((id) => !change.labelIds.includes(id));
    if (removed.length) {
      const names = qc.getQueryData<BoardSnapshot>(boardKey(boardId))?.labels.filter((l) => removed.includes(l.id)).map((l) => `„${l.name || 'ohne Namen'}“`);
      toast.info(`${removed.length === 1 ? 'Label' : 'Labels'} ${names?.join(', ')} entfernt.`, { label: 'Rückgängig', run: () => void save(before) });
    }
  };
}
